import { DismissDefault16Px, Divider, SearchDefault16Px, TextInput, blue, tokens } from '@galaxy-react/core';
import { useDebounceFn, useLocalStorageState } from 'ahooks';
import { useCombobox } from 'downshift';
import { AnimatePresence, motion } from 'framer-motion';
import { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRootTitle } from 'src/Shared/Utilities/Menu';
import EnterIcon from 'src/icons/enter.svg?react';
import { SearchNoResultGeneric, SearchPreview, SearchResult } from '../../Shared/lib';
import {
  SearchListBoxPanelOverflowStyled,
  SearchListBoxPanelPreviewStyled,
  SearchListBoxPanelWrapperStyled,
  SearchListBoxStyled,
} from './search-use-combobox.styled';

import { IReportItem } from '../Menu/menuModels';
import { ContentAfterStyled, IconButtonStyled } from './searchbox.styled';

import { useSearch } from 'src/Shared/Hooks/MenuHooks';
import { UserInfoContext } from 'src/app/App';
import { PreviousSearchResultStyled, RecentSearchStyled, Subheading } from './search-use-combobox.styled';

type SearchComboBoxProps = {
  onClose?: (name?: string) => void;
};

function ComboboxWithClearButton({ onClose = () => {} }: SearchComboBoxProps) {
  const navigate = useNavigate();
  const userInfo = useContext(UserInfoContext);
  const [searchHistory, setSearchHistory] = useLocalStorageState<IReportItem[]>(
    `report-search-history-${userInfo.userId}`,
    {
      defaultValue: [],
    },
  );

  const [searchKey, setSearchKey] = useState<string>('');
  const search = useSearch();
  const currentReportList = useMemo(() => {
    return search(searchKey);
  }, [searchKey, search]);

  const viewAllItem = useMemo(() => {
    return {
      value: 'viewAll',
      Title: '',
    };
  }, []);
  const [currentReportName, setCurrentReportName] = useState<string | undefined>();
  const currentReport = useMemo(() => {
    return currentReportList.find((item) => item.Name === currentReportName);
  }, [currentReportList, currentReportName]);
  const currentItems = useMemo(() => {
    return !searchKey ? searchHistory : [...currentReportList, viewAllItem];
  }, [searchKey, searchHistory, currentReportList, viewAllItem]);

  const { run: onInputValueChange } = useDebounceFn(
    (data) => {
      if (data.type !== '__input_keydown_enter__') {
        handleInputChange(data.inputValue);
      }
    },
    {
      wait: 300,
    },
  );
  const { run: onHighlightedIndexChange } = useDebounceFn(
    ({ highlightedIndex }) => {
      if (highlightedIndex === -1 || !searchKey) {
        setCurrentReportName('');
      } else {
        const item = currentReportList[highlightedIndex];
        setCurrentReportName(item?.Name);
      }
    },
    {
      wait: 100,
    },
  );
  const { reset, getMenuProps, getInputProps, getItemProps, highlightedIndex } = useCombobox({
    defaultIsOpen: true,
    items: currentItems,
    isOpen: true,
    onInputValueChange: onInputValueChange,
    onStateChange(changes) {
      // Handles the case where the user presses enter when no option is selected
      if (changes.type === '__input_keydown_enter__' && highlightedIndex === -1) {
        jumpToSearchList();
      }
    },
    // inputValue: searchKey,
    onHighlightedIndexChange: onHighlightedIndexChange,
    itemToString: (item) => item?.Title ?? '',
    onSelectedItemChange(data) {
      if (data.selectedItem) {
        if ((data.selectedItem as { value: string }).value === 'viewAll') {
          jumpToSearchList();
        } else if ((data.selectedItem as IReportItem).Path) {
          handleJumpToReport(data.selectedItem as IReportItem);
        }
      }
    },
  });
  // Animation variants
  const container = {
    hidden: { x: -4, opacity: 0 },
    show: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        delayChildren: 0.5,
      },
    },
  };

  const child = {
    hidden: { opacity: 0.5 },
    show: { opacity: 1, transition: { duration: 1 } },
  };

  const handleInputChange = (value) => {
    setCurrentReportName('');
    setSearchKey(value);
  };

  // maxinum of history is 5
  const handleSetHistory = (data: IReportItem) => {
    let newHistory = [];
    if (Array.isArray(searchHistory)) {
      if (searchHistory.findIndex((item) => item.Name === data.Name) >= 0) {
        newHistory = searchHistory.filter((item) => item.Name !== data.Name);
        newHistory.unshift(data);
        setSearchHistory(newHistory);
      } else {
        newHistory = [data, ...(searchHistory || []).slice(0, 4)];
      }
    } else {
      newHistory = [data];
    }
    setSearchHistory(newHistory);
  };
  const handleJumpToReport = (report: IReportItem) => {
    onClose(report?.Title);
    if (report) {
      handleSetHistory(report);
      navigate(report.Path!);
    }
  };
  const jumpToSearchList = () => {
    onClose(searchKey);
    navigate(`/search?searchKey=${searchKey}`);
  };

  return (
    <>
      <div style={{ padding: '8px 8px' }}>
        <TextInput
          autoFocus
          style={{ width: '100%' }}
          {...getInputProps()}
          // onKeyUp={handleKeyUp}
          // onChange={handleInputChange}
          // value={searchKey}
          contentBefore={<SearchDefault16Px />}
          contentAfter={
            <ContentAfterStyled>
              {searchKey && highlightedIndex === -1 && <EnterIcon></EnterIcon>}
              {searchKey && (
                <IconButtonStyled appearance="transparent" icon={<DismissDefault16Px />} onClick={() => reset()} />
              )}
            </ContentAfterStyled>
          }
        />
      </div>
      <div {...getMenuProps()}>
        {!searchKey ? (
          !!searchHistory?.length && (
            <PreviousSearchResultStyled>
              <Subheading variant="bodyStrong">Recent searches</Subheading>
              {(searchHistory || []).map((item, index) => {
                return (
                  <RecentSearchStyled
                    href={item.Path}
                    key={item.ReportId}
                    {...getItemProps({ item, index })}
                    style={{
                      background: highlightedIndex === index ? tokens.colorTransparentBackgroundHover : 'none',
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      handleJumpToReport(item);
                    }}
                  >
                    <SearchDefault16Px color={blue[700]} />
                    <span style={{ flex: 1 }}>{item.Title}</span>
                    {highlightedIndex === index && <EnterIcon></EnterIcon>}
                  </RecentSearchStyled>
                );
              })}
            </PreviousSearchResultStyled>
          )
        ) : currentReportList.length > 0 ? (
          <>
            <SearchListBoxStyled>
              <SearchListBoxPanelWrapperStyled>
                <SearchListBoxPanelOverflowStyled panelVisible={!!currentReport}>
                  {currentReportList.map((item, index) => (
                    <a onClick={(e) => e.preventDefault()} href={item.Path} key={item.ReportId}>
                      <SearchResult
                        {...getItemProps({ item, index })}
                        style={{
                          background: highlightedIndex === index ? tokens.colorTransparentBackgroundHover : 'none',
                        }}
                        key={item.ReportId}
                        text={item.Title ?? ''}
                        category={getRootTitle(item)}
                        description={item.MenuDescription}
                        // viewedNo={item.viewedNo}
                        // originalName={item.originalName}
                        isFavoured={item.IsFavourite}
                        icon={highlightedIndex === index && <EnterIcon></EnterIcon>}
                        onClick={() => handleJumpToReport(item)}
                      />
                    </a>
                  ))}
                </SearchListBoxPanelOverflowStyled>
                <AnimatePresence>
                  {currentReport ? (
                    <SearchListBoxPanelPreviewStyled>
                      <motion.div variants={container} initial="hidden" animate="show" key={currentReport?.Title}>
                        <motion.div variants={child} initial="hidden" animate="show" key="preview">
                          <SearchPreview
                            onClick={() => onClose(currentReport.Title)}
                            cardHeaderPreviewProps={{
                              reportName: currentReport.Title!,
                              description: currentReport.MenuDescription,
                              isFavoured: currentReport.IsFavourite,
                            }}
                            cardFooterPreviewProps={{
                              reportLink: currentReport.Path,
                              helpCenterLink: currentReport.ReportGuideUrl,
                            }}
                          />
                        </motion.div>
                      </motion.div>
                    </SearchListBoxPanelPreviewStyled>
                  ) : null}
                </AnimatePresence>
              </SearchListBoxPanelWrapperStyled>
            </SearchListBoxStyled>
            <Divider style={{ marginTop: 8 }}></Divider>
            <RecentSearchStyled
              key="viewAll"
              {...getItemProps({
                key: 'viewAll',
                item: viewAllItem,
                index: currentReportList.length,
              })}
              style={{
                background:
                  highlightedIndex === currentReportList.length ? tokens.colorTransparentBackgroundHover : 'none',
                cursor: 'pointer',
                padding: '16px 18px',
              }}
            >
              <SearchDefault16Px color={blue[700]} />
              <span style={{ flex: 1 }}>View all search results</span>
              {highlightedIndex === currentReportList.length && <EnterIcon></EnterIcon>}
            </RecentSearchStyled>
          </>
        ) : (
          <SearchNoResultGeneric />
        )}
      </div>
    </>
  );
}

export default ComboboxWithClearButton;
